import { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import WebMercatorViewport from 'viewport-mercator-project';
import { Map, PolygonArea } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import {
  buildPolygonStyle,
  calculateBounds,
  extractCoordinatesFromGeometry,
} from 'lib/map';
import { atom } from 'jotai';
import { MapUserConfig } from 'components/map/types';
import { InterestComparison } from './atoms';
import style from './comprehensive-map.module.scss';
import { Popup } from 'react-map-gl';
import {
  useMapHoverHandler,
  LayerProcessor,
} from 'lib/hooks/useMapHoverHandler';

const MAP_WIDTH = 1200;
const MAP_HEIGHT = 700;
const VIEWPORT_PADDING = 60;

// Map color constants - Mapbox doesn't support Carbon tokens, so we define colors here
const MAP_COLORS = {
  // Green - matched comparisons
  matchedValidator: { fill: 'rgb(34, 197, 94)', line: 'rgb(22, 163, 74)' },
  matchedEta: { fill: 'rgb(134, 239, 172)', line: 'rgb(34, 197, 94)' },
  // Red - failed/mismatched comparisons
  failedValidator: { fill: 'rgb(239, 68, 68)', line: 'rgb(220, 38, 38)' },
  failedEta: { fill: 'rgb(252, 165, 165)', line: 'rgb(239, 68, 68)' },
  // Orange - no overlap found
  noOverlapValidator: { fill: 'rgb(251, 146, 60)', line: 'rgb(234, 88, 12)' },
  noOverlapShape: { fill: 'rgb(253, 186, 116)', line: 'rgb(251, 146, 60)' },
  // Blue - validator input locations (reference layer)
  inputLocation: { fill: 'rgb(147, 197, 253)', line: 'rgb(96, 165, 250)' },
} as const;

const MATCHED_VALIDATOR_STYLE = buildPolygonStyle({
  color: MAP_COLORS.matchedValidator.fill,
  lineColor: MAP_COLORS.matchedValidator.line,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 2,
});

const MATCHED_ETA_STYLE = buildPolygonStyle({
  color: MAP_COLORS.matchedEta.fill,
  lineColor: MAP_COLORS.matchedEta.line,
  fillOpacity: 0.3,
  lineOpacity: 1,
  lineWidth: 2,
});

// Red for failed comparisons
const FAILED_VALIDATOR_STYLE = buildPolygonStyle({
  color: MAP_COLORS.failedValidator.fill,
  lineColor: MAP_COLORS.failedValidator.line,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 2,
});

const FAILED_ETA_STYLE = buildPolygonStyle({
  color: MAP_COLORS.failedEta.fill,
  lineColor: MAP_COLORS.failedEta.line,
  fillOpacity: 0.3,
  lineOpacity: 1,
  lineWidth: 2,
});

// Orange for validator geometries with no overlaps
const NO_OVERLAP_VALIDATOR_STYLE = buildPolygonStyle({
  color: MAP_COLORS.noOverlapValidator.fill,
  lineColor: MAP_COLORS.noOverlapValidator.line,
  fillOpacity: 0.4,
  lineOpacity: 1,
  lineWidth: 2,
});

// Blue for validator input locations (reference layer)
const VALIDATOR_INPUT_LOCATION_STYLE = buildPolygonStyle({
  color: MAP_COLORS.inputLocation.fill,
  lineColor: MAP_COLORS.inputLocation.line,
  fillOpacity: 0.15,
  lineOpacity: 0.7,
  lineWidth: 1.5,
});

const NO_OVERLAP_SHAPE_STYLE = buildPolygonStyle({
  color: MAP_COLORS.noOverlapShape.fill,
  lineColor: MAP_COLORS.noOverlapShape.line,
  fillOpacity: 0.3,
  lineOpacity: 1,
  lineWidth: 2,
});

interface ComprehensiveMapProps {
  comparisonResults: InterestComparison[];
  validatorGeometries: Record<string, GeoJSON.Geometry>;
  validatorInputLocations?: Record<string, GeoJSON.Geometry>;
  shapeGeometries: Record<string, { geometry: GeoJSON.Geometry; name: string }>;
  isOpen: boolean;
  onToggle: () => void;
}

export const ComprehensiveMap = ({
  comparisonResults,
  validatorGeometries,
  validatorInputLocations = {},
  shapeGeometries,
  isOpen,
}: ComprehensiveMapProps) => {
  const [showMatchedValidator, setShowMatchedValidator] = useState(true);
  const [showMatchedShapes, setShowMatchedShapes] = useState(true);
  const [showFailedValidator, setShowFailedValidator] = useState(true);
  const [showFailedShapes, setShowFailedShapes] = useState(true);
  const [showNoOverlapValidator, setShowNoOverlapValidator] = useState(true);
  const [showNoOverlapShapes, setShowNoOverlapShapes] = useState(true);
  const [showValidatorInputLocations, setShowValidatorInputLocations] =
    useState(true);

  const getShapeNameById = useCallback(
    (shapeId: string): string => {
      return shapeGeometries[shapeId]?.name || `Shape ${shapeId}`;
    },
    [shapeGeometries]
  );

  type GeometryFeature = {
    type: 'validator' | 'shape' | 'input-location';
    name: string;
    id: string;
    category: 'matched' | 'failed' | 'no-overlap' | 'input-location';
  };

  const layerProcessors: LayerProcessor<GeometryFeature>[] = useMemo(
    () => [
      {
        prefix: 'polygon-fill-matched-validator-',
        isVisible: showMatchedValidator,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-matched-validator-', ''),
        buildFeature: (name) => ({
          type: 'validator' as const,
          name,
          id: name,
          category: 'matched' as const,
        }),
      },
      {
        prefix: 'polygon-fill-failed-validator-',
        isVisible: showFailedValidator,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-failed-validator-', ''),
        buildFeature: (name) => ({
          type: 'validator' as const,
          name,
          id: name,
          category: 'failed' as const,
        }),
      },
      {
        prefix: 'polygon-fill-no-comparison-validator-',
        isVisible: showNoOverlapValidator,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-no-comparison-validator-', ''),
        buildFeature: (name) => ({
          type: 'validator' as const,
          name,
          id: name,
          category: 'no-overlap' as const,
        }),
      },
      {
        prefix: 'polygon-fill-matched-eta-',
        isVisible: showMatchedShapes,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-matched-eta-', ''),
        buildFeature: (shapeId) => ({
          type: 'shape' as const,
          name: getShapeNameById(shapeId),
          id: shapeId,
          category: 'matched' as const,
        }),
      },
      {
        prefix: 'polygon-fill-failed-eta-',
        isVisible: showFailedShapes,
        extractId: (layerId) => layerId.replace('polygon-fill-failed-eta-', ''),
        buildFeature: (shapeId) => ({
          type: 'shape' as const,
          name: getShapeNameById(shapeId),
          id: shapeId,
          category: 'failed' as const,
        }),
      },
      {
        prefix: 'polygon-fill-no-comparison-shape-',
        isVisible: showNoOverlapShapes,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-no-comparison-shape-', ''),
        buildFeature: (shapeId) => ({
          type: 'shape' as const,
          name: getShapeNameById(shapeId),
          id: shapeId,
          category: 'no-overlap' as const,
        }),
      },
      {
        prefix: 'polygon-fill-validator-input-',
        isVisible: showValidatorInputLocations,
        extractId: (layerId) =>
          layerId.replace('polygon-fill-validator-input-', ''),
        buildFeature: (locationName) => ({
          type: 'input-location' as const,
          name: locationName,
          id: locationName,
          category: 'input-location' as const,
        }),
      },
    ],
    [
      showMatchedValidator,
      showFailedValidator,
      showNoOverlapValidator,
      showMatchedShapes,
      showFailedShapes,
      showNoOverlapShapes,
      showValidatorInputLocations,
      getShapeNameById,
    ]
  );

  const { hoverInfo, onMouseMove, onMouseLeave } =
    useMapHoverHandler(layerProcessors);

  const getCategoryColors = (
    category: 'matched' | 'failed' | 'no-overlap' | 'input-location',
    type: 'validator' | 'shape' | 'input-location'
  ): {
    fillColor: string;
    borderColor: string;
  } => {
    if (type === 'input-location' || category === 'input-location') {
      return {
        fillColor: MAP_COLORS.inputLocation.fill,
        borderColor: MAP_COLORS.inputLocation.line,
      };
    }
    if (type === 'validator') {
      if (category === 'matched') {
        return {
          fillColor: MAP_COLORS.matchedValidator.fill,
          borderColor: MAP_COLORS.matchedValidator.line,
        };
      } else if (category === 'failed') {
        return {
          fillColor: MAP_COLORS.failedValidator.fill,
          borderColor: MAP_COLORS.failedValidator.line,
        };
      } else {
        // no-overlap
        return {
          fillColor: MAP_COLORS.noOverlapValidator.fill,
          borderColor: MAP_COLORS.noOverlapValidator.line,
        };
      }
    } else {
      // shape
      if (category === 'matched') {
        return {
          fillColor: MAP_COLORS.matchedEta.fill,
          borderColor: MAP_COLORS.matchedEta.line,
        };
      } else if (category === 'failed') {
        return {
          fillColor: MAP_COLORS.failedEta.fill,
          borderColor: MAP_COLORS.failedEta.line,
        };
      } else {
        // no-overlap
        return {
          fillColor: MAP_COLORS.noOverlapShape.fill,
          borderColor: MAP_COLORS.noOverlapShape.line,
        };
      }
    }
  };

  const mapConfigAtom = useMemo(() => {
    const defaultConfig = getMapDefaultConfig();
    return atom({
      ...defaultConfig,
      wells: { ...defaultConfig.wells, checked: false },
      wellsV2: { ...defaultConfig.wellsV2, checked: false },
      developmentAreas: {
        ...defaultConfig.developmentAreas,
        options: {
          ...defaultConfig.developmentAreas.options,
          horizontalUnits: false,
          verticalUnits: false,
          fieldUnits: false,
          pricing: false,
        } as MapUserConfig['developmentAreas']['options'],
      },
      titleEntityAssets: { ...defaultConfig.titleEntityAssets, checked: false },
      basinTier: { ...defaultConfig.basinTier, checked: false },
      scrollZoom: { checked: true },
      popups: { checked: false },
      focusAreas: { checked: false, selections: {} },
    });
  }, []);

  const mapViewState = useMemo(() => {
    try {
      const allCoordinates: number[][] = [];

      Object.values(validatorGeometries || {}).forEach((geom) => {
        allCoordinates.push(...extractCoordinatesFromGeometry(geom));
      });

      Object.values(shapeGeometries || {}).forEach((shapeData) => {
        allCoordinates.push(
          ...extractCoordinatesFromGeometry(shapeData.geometry)
        );
      });

      Object.values(validatorInputLocations || {}).forEach((geom) => {
        allCoordinates.push(...extractCoordinatesFromGeometry(geom));
      });

      if (allCoordinates.length === 0) return null;

      const bounds = calculateBounds(allCoordinates);
      const mercator = new WebMercatorViewport({
        width: MAP_WIDTH,
        height: MAP_HEIGHT,
      });

      const viewState = mercator.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING }
      );

      return {
        longitude: viewState.longitude,
        latitude: viewState.latitude,
        zoom: viewState.zoom,
      };
    } catch (error) {
      console.error('Error calculating comprehensive map view:', error);
      return null;
    }
  }, [validatorGeometries, shapeGeometries, validatorInputLocations]);

  const categorizedGeometries = useMemo(() => {
    const matchedValidatorGeoms = new Set<string>();
    const failedValidatorGeoms = new Set<string>();
    const matchedShapeIds = new Set<string>();
    const failedShapeIds = new Set<string>();

    comparisonResults.forEach((comparison) => {
      // Match = both interest values AND shape similarity match
      const isMatch = comparison.match && comparison.shapeMatch;
      if (isMatch) {
        matchedValidatorGeoms.add(comparison.geometryName);
        comparison.shapeIds.forEach((id) => matchedShapeIds.add(String(id)));
      } else {
        // Mismatched = shape doesn't match OR interests don't match
        failedValidatorGeoms.add(comparison.geometryName);
        comparison.shapeIds.forEach((id) => failedShapeIds.add(String(id)));
      }
    });

    const validatorGeomNames = Object.keys(validatorGeometries);
    const noComparisonValidatorGeoms = validatorGeomNames.filter(
      (name) =>
        !matchedValidatorGeoms.has(name) && !failedValidatorGeoms.has(name)
    );

    const shapeIds = Object.keys(shapeGeometries);
    const noComparisonShapeIds = shapeIds.filter(
      (id) => !matchedShapeIds.has(id) && !failedShapeIds.has(id)
    );

    return {
      matchedValidatorGeoms,
      failedValidatorGeoms,
      noComparisonValidatorGeoms,
      matchedShapeIds,
      failedShapeIds,
      noComparisonShapeIds,
      matchedCount: matchedValidatorGeoms.size,
      failedCount: failedValidatorGeoms.size + failedShapeIds.size,
      noOverlapCount:
        noComparisonValidatorGeoms.length + noComparisonShapeIds.length,
    };
  }, [comparisonResults, validatorGeometries, shapeGeometries]);

  if (!isOpen) {
    return null;
  }

  if (!mapViewState) {
    return (
      <div className={style.container}>
        <div className={style.empty}>No geometry data available to display</div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.legend}>
        <div className={style.legendSection}>
          <div className={style.legendTitle}>Matched Comparisons</div>
          <div className={style.legendItems}>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showMatchedValidator,
                [style.legendItemInactive]: !showMatchedValidator,
              })}
              onClick={() => setShowMatchedValidator(!showMatchedValidator)}
            >
              <div className={style.legendColorMatchedValidator} />
              <span>
                Title Validator Output Shape (
                {categorizedGeometries.matchedValidatorGeoms.size})
              </span>
            </button>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showMatchedShapes,
                [style.legendItemInactive]: !showMatchedShapes,
              })}
              onClick={() => setShowMatchedShapes(!showMatchedShapes)}
            >
              <div className={style.legendColorMatchedEta} />
              <span>
                Entity Title Asset Shape (
                {categorizedGeometries.matchedShapeIds.size})
              </span>
            </button>
          </div>
        </div>
        <div className={style.legendSection}>
          <div className={style.legendTitle}>Mismatched Comparisons</div>
          <div className={style.legendItems}>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showFailedValidator,
                [style.legendItemInactive]: !showFailedValidator,
              })}
              onClick={() => setShowFailedValidator(!showFailedValidator)}
            >
              <div className={style.legendColorFailedValidator} />
              <span>
                Title Validator Output Shape (
                {categorizedGeometries.failedValidatorGeoms.size})
              </span>
            </button>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showFailedShapes,
                [style.legendItemInactive]: !showFailedShapes,
              })}
              onClick={() => setShowFailedShapes(!showFailedShapes)}
            >
              <div className={style.legendColorFailedEta} />
              <span>
                Entity Title Asset Shape (
                {categorizedGeometries.failedShapeIds.size})
              </span>
            </button>
          </div>
        </div>
        <div className={style.legendSection}>
          <div className={style.legendTitle}>No Overlaps Found</div>
          <div className={style.legendItems}>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showNoOverlapValidator,
                [style.legendItemInactive]: !showNoOverlapValidator,
              })}
              onClick={() => setShowNoOverlapValidator(!showNoOverlapValidator)}
            >
              <div className={style.legendColorNoOverlapValidator} />
              <span>
                Title Validator Output Shape (
                {categorizedGeometries.noComparisonValidatorGeoms.length})
              </span>
            </button>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showNoOverlapShapes,
                [style.legendItemInactive]: !showNoOverlapShapes,
              })}
              onClick={() => setShowNoOverlapShapes(!showNoOverlapShapes)}
            >
              <div className={style.legendColorNoOverlapShape} />
              <span>
                Entity Title Asset Shape (
                {categorizedGeometries.noComparisonShapeIds.length})
              </span>
            </button>
          </div>
        </div>
        <div className={style.legendSection}>
          <div className={style.legendTitle}>
            Title Validator Input Locations
          </div>
          <div className={style.legendItems}>
            <button
              type="button"
              className={classNames(style.legendItem, {
                [style.legendItemActive]: showValidatorInputLocations,
                [style.legendItemInactive]: !showValidatorInputLocations,
              })}
              onClick={() =>
                setShowValidatorInputLocations(!showValidatorInputLocations)
              }
            >
              <div className={style.legendColorValidatorInput} />
              <span>
                Title Validator Input Location (
                {Object.keys(validatorInputLocations).length})
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className={style.mapContainer}>
        <Map
          longitude={mapViewState.longitude}
          latitude={mapViewState.latitude}
          zoom={mapViewState.zoom}
          mapConfigAtom={mapConfigAtom}
          showControls={true}
          showMapFilters={true}
          interactiveLayerIds={[
            ...(showMatchedValidator
              ? Array.from(categorizedGeometries.matchedValidatorGeoms).map(
                  (name) => `polygon-fill-matched-validator-${name}`
                )
              : []),
            ...(showFailedValidator
              ? Array.from(categorizedGeometries.failedValidatorGeoms).map(
                  (name) => `polygon-fill-failed-validator-${name}`
                )
              : []),
            ...(showNoOverlapValidator
              ? categorizedGeometries.noComparisonValidatorGeoms.map(
                  (name) => `polygon-fill-no-comparison-validator-${name}`
                )
              : []),
            ...(showMatchedShapes
              ? Array.from(categorizedGeometries.matchedShapeIds).map(
                  (id) => `polygon-fill-matched-eta-${id}`
                )
              : []),
            ...(showFailedShapes
              ? Array.from(categorizedGeometries.failedShapeIds).map(
                  (id) => `polygon-fill-failed-eta-${id}`
                )
              : []),
            ...(showNoOverlapShapes
              ? categorizedGeometries.noComparisonShapeIds.map(
                  (id) => `polygon-fill-no-comparison-shape-${id}`
                )
              : []),
            ...(showValidatorInputLocations
              ? Object.keys(validatorInputLocations).map(
                  (id) => `polygon-fill-validator-input-${id}`
                )
              : []),
          ]}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          {/* Render matched validator geometries */}
          {showMatchedValidator &&
            Array.from(categorizedGeometries.matchedValidatorGeoms).map(
              (geomName) => {
                const geom = validatorGeometries[geomName];
                return (
                  <PolygonArea
                    key={`matched-validator-${geomName}`}
                    id={`matched-validator-${geomName}`}
                    geometry={geom}
                    style={MATCHED_VALIDATOR_STYLE}
                  />
                );
              }
            )}

          {/* Render matched ETA shapes */}
          {showMatchedShapes &&
            Array.from(categorizedGeometries.matchedShapeIds).map((shapeId) => (
              <PolygonArea
                key={`matched-eta-${shapeId}`}
                id={`matched-eta-${shapeId}`}
                geometry={shapeGeometries[shapeId].geometry}
                style={MATCHED_ETA_STYLE}
              />
            ))}

          {/* Render failed validator geometries */}
          {showFailedValidator &&
            Array.from(categorizedGeometries.failedValidatorGeoms).map(
              (geomName) => (
                <PolygonArea
                  key={`failed-validator-${geomName}`}
                  id={`failed-validator-${geomName}`}
                  geometry={validatorGeometries[geomName]}
                  style={FAILED_VALIDATOR_STYLE}
                />
              )
            )}

          {/* Render failed ETA shapes */}
          {showFailedShapes &&
            Array.from(categorizedGeometries.failedShapeIds).map((shapeId) => (
              <PolygonArea
                key={`failed-eta-${shapeId}`}
                id={`failed-eta-${shapeId}`}
                geometry={shapeGeometries[shapeId].geometry}
                style={FAILED_ETA_STYLE}
              />
            ))}

          {/* Render validator geometries with no comparisons */}
          {showNoOverlapValidator &&
            categorizedGeometries.noComparisonValidatorGeoms.map((geomName) => (
              <PolygonArea
                key={`no-comparison-validator-${geomName}`}
                id={`no-comparison-validator-${geomName}`}
                geometry={validatorGeometries[geomName]}
                style={NO_OVERLAP_VALIDATOR_STYLE}
              />
            ))}

          {/* Render shapes with no comparisons */}
          {showNoOverlapShapes &&
            categorizedGeometries.noComparisonShapeIds.map((shapeId) => (
              <PolygonArea
                key={`no-comparison-shape-${shapeId}`}
                id={`no-comparison-shape-${shapeId}`}
                geometry={shapeGeometries[shapeId].geometry}
                style={NO_OVERLAP_SHAPE_STYLE}
              />
            ))}

          {/* Render validator input locations (reference layer) */}
          {showValidatorInputLocations &&
            Object.entries(validatorInputLocations).map(([name, geometry]) => (
              <PolygonArea
                key={`validator-input-${name}`}
                id={`validator-input-${name}`}
                geometry={geometry}
                style={VALIDATOR_INPUT_LOCATION_STYLE}
              />
            ))}

          {/* Hover tooltip */}
          {hoverInfo && (
            <Popup
              longitude={hoverInfo.longitude}
              latitude={hoverInfo.latitude}
              closeButton={false}
              closeOnClick={false}
              className={style.hoverPopup}
            >
              <div className={style.popupContent}>
                {hoverInfo.features.map((feature, idx) => {
                  const { fillColor, borderColor } = getCategoryColors(
                    feature.category,
                    feature.type
                  );
                  return (
                    <div
                      key={`${feature.id}-${idx}`}
                      className={style.popupItem}
                    >
                      <span
                        className={style.legendSquare}
                        style={{
                          backgroundColor: fillColor,
                          borderColor: borderColor,
                        }}
                      />
                      <span>{feature.name}</span>
                    </div>
                  );
                })}
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </div>
  );
};
